import {
  default as how_to_image_to_pdf,
  default as how_to_pdf_to_word
} from 'assets/img/how-to-section/how-to-new-design-image-to-pdf.svg';
import { default as how_to_main } from 'assets/img/how-to-section/how-to-new-design-main.svg';
import how_to_pdf_to_excel from 'assets/img/how-to-section/how-to-new-design-pdf-to-excel.svg';
import how_to_pdf_to_png from 'assets/img/how-to-section/how-to-new-design-pdf-to-png.svg';
import how_to_pdf_to_pptx from 'assets/img/how-to-section/how-to-new-design-pdf-to-pptx.svg';
import icon_1 from 'assets/img/how-to-section/icon_1.svg';
import icon_2 from 'assets/img/how-to-section/icon_2.svg';
import icon_3 from 'assets/img/how-to-section/icon_3.svg';
import type { TFunction } from 'i18next';

import { EFunnels } from 'ts/enums/funnel';
import type { IService } from 'ts/interfaces/services/service';
import { EServiceType } from 'ts/interfaces/services/service';
import { InternalFileType } from 'ts/types/file';

import image_to_pdf from './assets/image_to_pdf.svg';
import pdf_converter from './assets/pdf_converter.svg';
import pdf_to_excel from './assets/pdf_to_excel.svg';
import pdf_to_pptx from './assets/pdf_to_pptx.svg';
import pdf_to_word from './assets/pdf_to_word.svg';
import { EEditorServices } from './editorServicesList';
import { mergeImagesPageService } from './mergeImageServiceList';
import { mergePageService } from './mergePageService';

const pdfToWordService = (t: TFunction): IService => ({
  key: 'pdf-to-word',
  serviceType: EServiceType.CONVERTOR,
  path: 'pdf-to-word',
  mainColor: 'dark-blue',
  uploadSectionImage: pdf_to_word,
  uploadSection: {
    from: InternalFileType.PDF,
    to: InternalFileType.DOCX,
    headline: t('landing.pdf_to_word.h1.title'),
    subtitle_1: t('landing.pdf_to_word.h1.subtitle')
  },
  banner: {
    headline: t('landing.pdf_to_word.CTA.title'),
    subtitle: t('landing.pdf_to_word.CTA.subtitle'),
    format: InternalFileType.PDF,
    buttonLabel: t('landing.pdf_to_word.CTA.buttonLabel')
  },
  howTo: {
    headline_1: t('landing.pdf_to_word.how_to.h2'),
    img: how_to_pdf_to_word,
    description: [
      {
        icon: icon_1,
        text: t('landing.pdf_to_word.how_to.first')
      },
      {
        icon: icon_2,
        text: t('landing.pdf_to_word.how_to.second')
      },
      {
        icon: icon_3,
        text: t('landing.pdf_to_word.how_to.third')
      }
    ]
  },
  title: t('meta.pdf_to_word.title'),
  description: t('meta.pdf_to_word.description'),
  fag: {
    question: 'Convert a PDF file to Word online in a few steps!',
    answer:
      '1. To convert a PDF file, upload it using the button "Upload" above or Drag&Drop. 2. Edit your PDF document using our tools and convert it to Word with one click. 3. Download or share the Word file.'
  },
  name: t('meta.pdf_to_word.title'),
  nameKey: t('meta.pdf_to_word.title'),
  from: InternalFileType.PDF,
  to: InternalFileType.DOCX,
  availableFormats: '.pdf',
  funnel: EFunnels.PDF_TO_WORD
});

const imageToPdfService = (t: TFunction): IService => ({
  serviceType: EServiceType.CONVERTOR,
  path: '/image-to-pdf',
  key: 'image-to-pdf',
  funnel: EFunnels.IMAGE_TO_PDF,
  uploadSectionImage: image_to_pdf,
  name: t('meta.image_to_pdf.title'),
  nameKey: 'landing.image_to_pdf.name',
  title: t('meta.image_to_pdf.title'),
  description: t('meta.image_to_pdf.description'),
  from: [
    InternalFileType.SVG,
    InternalFileType.HEIC,
    InternalFileType.PNG,
    InternalFileType.BMP,
    InternalFileType.EPS,
    InternalFileType.GIF,
    InternalFileType.TIFF,
    InternalFileType.WEBP,
    InternalFileType.JPG,
    InternalFileType.JPEG
  ],
  to: InternalFileType.PDF,
  availableFormats: '.jpg, .jpeg, .png, .gif, .webp, .bmp',
  mainColor: 'orange',
  uploadSection: {
    from: [
      InternalFileType.HEIC,
      InternalFileType.SVG,
      InternalFileType.PNG,
      InternalFileType.BMP,
      InternalFileType.EPS,
      InternalFileType.GIF,
      InternalFileType.TIFF,
      InternalFileType.WEBP,
      InternalFileType.JPG,
      InternalFileType.JPEG
    ],
    to: InternalFileType.PDF,
    headline: t('landing.image_to_pdf.h1.title'),
    subtitle_1: t('landing.image_to_pdf.h1.subtitle')
  },
  banner: {
    headline: t('landing.image_to_pdf.CTA.title'),
    subtitle: t('landing.image_to_pdf.CTA.subtitle'),
    format: [
      InternalFileType.SVG,
      InternalFileType.HEIC,
      InternalFileType.PNG,
      InternalFileType.BMP,
      InternalFileType.EPS,
      InternalFileType.GIF,
      InternalFileType.TIFF,
      InternalFileType.WEBP,
      InternalFileType.JPG,
      InternalFileType.JPEG
    ],
    buttonLabel: t('landing.image_to_pdf.CTA.buttonLabel')
  },
  howTo: {
    headline_1: t('landing.image_to_pdf.how_to.h2'),
    subtitle_1: t('landing.image_to_pdf.h1.subtitle'),
    img: how_to_image_to_pdf,
    description: [
      {
        icon: icon_1,
        text: t('landing.image_to_pdf.how_to.first')
      },
      {
        icon: icon_2,
        text: t('landing.image_to_pdf.how_to.second')
      },
      {
        icon: icon_3,
        text: t('landing.image_to_pdf.how_to.third')
      }
    ]
  },
  fag: {
    question: 'Convert a Image to PDF online in a few steps!',
    answer:
      '1. To convert Image, upload it using the button "Upload" above or Drag&Drop. 2. Edit your Image document using our tools and convert it to PDF with one click. 3. Download or share the PDF file.'
  }
});

const pdfToExcelService = (t: TFunction): IService => ({
  serviceType: EServiceType.CONVERTOR,
  path: 'pdf-to-excel',
  key: 'pdf-to-excel',
  funnel: EFunnels.PDF_TO_EXCEL,
  uploadSectionImage: pdf_to_excel,
  name: t('meta.pdf_to_excel.title'),
  nameKey: 'landing.pdf_to_excel.name',
  title: t('meta.pdf_to_excel.title'),
  description: t('meta.pdf_to_excel.description'),
  from: InternalFileType.PDF,
  to: InternalFileType.XLS,
  availableFormats: '.pdf',
  mainColor: 'green',
  uploadSection: {
    from: InternalFileType.PDF,
    to: InternalFileType.XLS,
    headline: t('landing.pdf_to_excel.h1.title'),
    subtitle_1: t('landing.pdf_to_excel.h1.subtitle')
  },
  banner: {
    headline: t('landing.pdf_to_excel.CTA.title'),
    subtitle: t('landing.pdf_to_excel.CTA.subtitle'),
    format: InternalFileType.PDF,
    buttonLabel: t('landing.pdf_to_excel.CTA.buttonLabel')
  },
  howTo: {
    headline_1: t('landing.pdf_to_excel.how_to.h2'),
    subtitle_1: t('landing.pdf_to_excel.h1.subtitle'),
    img: how_to_pdf_to_excel,
    description: [
      {
        icon: icon_1,
        text: t('landing.pdf_to_excel.how_to.first')
      },
      {
        icon: icon_2,
        text: t('landing.pdf_to_excel.how_to.second')
      },
      {
        icon: icon_3,
        text: t('landing.pdf_to_excel.how_to.third')
      }
    ]
  },
  fag: {
    question: 'Convert a PDF file to Word online in a few steps!',
    answer:
      '1. To convert a PDF file, upload it using the button "Upload" above or Drag&Drop. 2. Edit your PDF document using our tools and convert it to Word with one click. 3. Download or share the Word file.'
  }
});

const pdfToPptxService = (t: TFunction): IService => ({
  serviceType: EServiceType.CONVERTOR,
  path: 'pdf-to-pptx',
  key: 'pdf-to-pptx',
  funnel: EFunnels.PDF_TO_PPTX,
  uploadSectionImage: pdf_to_pptx,
  name: t('meta.pdf_to_pptx.title'),
  nameKey: 'landing.pdf_to_pptx.name',
  from: InternalFileType.PDF,
  to: InternalFileType.PPTX,
  availableFormats: '.pdf',
  title: t('meta.pdf_to_pptx.title'),
  description: t('meta.pdf_to_pptx.description'),
  mainColor: 'peach',
  uploadSection: {
    from: InternalFileType.PDF,
    to: InternalFileType.PPTX,
    headline: t('landing.pdf_to_pptx.h1.title'),
    subtitle_1: t('landing.pdf_to_pptx.h1.subtitle')
  },
  banner: {
    headline: t('landing.pdf_to_pptx.CTA.title'),
    subtitle: t('landing.pdf_to_pptx.CTA.subtitle'),
    format: InternalFileType.PDF,
    buttonLabel: t('landing.pdf_to_pptx.CTA.buttonLabel')
  },
  howTo: {
    headline_1: t('landing.pdf_to_pptx.how_to.h2'),
    subtitle_1: t('landing.pdf_to_pptx.h1.subtitle'),
    img: how_to_pdf_to_pptx,
    description: [
      {
        icon: icon_1,
        text: t('landing.pdf_to_pptx.how_to.first')
      },
      {
        icon: icon_2,
        text: t('landing.pdf_to_pptx.how_to.second')
      },
      {
        icon: icon_3,
        text: t('landing.pdf_to_pptx.how_to.third')
      }
    ]
  },
  fag: {
    question: 'Convert a PDF file to Word online in a few steps!',
    answer:
      '1. To convert a PDF file, upload it using the button "Upload" above or Drag&Drop. 2. Edit your PDF document using our tools and convert it to Word with one click. 3. Download or share the Word file.'
  }
});

export const pdfConverterService = (t: TFunction): IService => ({
  serviceType: EServiceType.PDF_CONVERTOR,
  path: 'pdf-converter',
  key: EEditorServices.PDF_CONVERTOR,
  uploadSectionImage: pdf_converter,
  from: InternalFileType.PDF,
  to: InternalFileType.PDF,
  funnel: EFunnels.PDF_CONVERTER,
  name: t('meta.pdf_converter.title'),
  nameKey: 'landing.pdf_converter.name',
  availableFormats: '.pdf',
  mainColor: 'berry',
  uploadSection: {
    from: InternalFileType.PDF,
    to: InternalFileType.PDF,
    headline: t('landing.converter_pdf.h1.title'),
    subtitle_1: t('landing.converter_pdf.h1.subtitle')
  },
  banner: {
    format: InternalFileType.PDF,
    headline: t('landing.converter_pdf.CTA.title'),
    subtitle: t('landing.converter_pdf.CTA.subtitle'),
    defaultFileFormat: InternalFileType.PDF,
    buttonLabel: t('landing.converter_pdf.CTA.buttonLabel')
  },
  howTo: {
    headline_1: t('landing.converter_pdf.how_to.h2'),
    img: how_to_main,
    description: [
      {
        icon: icon_1,
        text: t('landing.converter_pdf.how_to.first')
      },
      {
        icon: icon_2,
        text: t('landing.converter_pdf.how_to.second')
      },
      {
        icon: icon_3,
        text: t('landing.converter_pdf.how_to.third')
      }
    ]
  },
  fag: {
    question: 'Edit your PDF file online in a few steps!',
    answer:
      '1. Open PDF Guru and drop the PDF file you want to modify. 2. Add elements (text, images, etc.) using the editor. 3. Approve the modified document, download or share it.'
  }
});

export const pdfToJpgService = (t: TFunction): IService => ({
  key: 'pdf-to-jpg',
  serviceType: EServiceType.CONVERTOR,
  path: 'pdf-to-jpg',
  mainColor: 'dark-blue',
  uploadSectionImage: pdf_to_word,
  uploadSection: {
    from: InternalFileType.PDF,
    to: InternalFileType.JPG,
    headline: t('landing.pdf_to_jpg.h1.title'),
    subtitle_1: t('landing.pdf_to_jpg.h1.subtitle')
  },
  banner: {
    headline: t('landing.pdf_to_jpg.CTA.title'),
    subtitle: t('landing.pdf_to_jpg.CTA.subtitle'),
    format: InternalFileType.PDF,
    buttonLabel: t('landing.pdf_to_jpg.CTA.buttonLabel')
  },
  howTo: {
    headline_1: t('landing.pdf_to_jpg.how_to.h2'),
    img: how_to_pdf_to_word,
    description: [
      {
        icon: icon_1,
        text: t('landing.pdf_to_jpg.how_to.first')
      },
      {
        icon: icon_2,
        text: t('landing.pdf_to_jpg.how_to.second')
      },
      {
        icon: icon_3,
        text: t('landing.pdf_to_jpg.how_to.third')
      }
    ]
  },
  title: t('meta.pdf_to_jpg.title'),
  description: t('meta.pdf_to_jpg.description'),
  fag: {
    question: 'Convert a PDF file to JPG online in a few steps!',
    answer:
      '1. To convert a PDF file, upload it using the button "Upload" above or Drag&Drop. 2. Edit your PDF document using our tools and convert it to JPG with one click. 3. Download or share the JPG file.'
  },
  name: t('meta.pdf_to_jpg.title'),
  nameKey: t('meta.pdf_to_jpg.title'),
  from: InternalFileType.PDF,
  to: InternalFileType.JPG,
  availableFormats: '.pdf',
  funnel: EFunnels.PDF_TO_JPG
});

const jpgToPdfService = (t: TFunction): IService => ({
  key: 'jpg-to-pdf',
  serviceType: EServiceType.CONVERTOR,
  path: 'jpg-to-pdf',
  mainColor: 'dark-blue',
  uploadSectionImage: pdf_to_word,
  uploadSection: {
    from: InternalFileType.JPG,
    to: InternalFileType.PDF,
    headline: t('landing.jpg_to_pdf.h1.title'),
    subtitle_1: t('landing.jpg_to_pdf.h1.subtitle')
  },
  banner: {
    headline: t('landing.jpg_to_pdf.CTA.title'),
    subtitle: t('landing.jpg_to_pdf.CTA.subtitle'),
    format: InternalFileType.JPG,
    buttonLabel: t('landing.jpg_to_pdf.CTA.buttonLabel')
  },
  howTo: {
    headline_1: t('landing.jpg_to_pdf.how_to.h2'),
    img: how_to_pdf_to_word,
    description: [
      {
        icon: icon_1,
        text: t('landing.jpg_to_pdf.how_to.first')
      },
      {
        icon: icon_2,
        text: t('landing.jpg_to_pdf.how_to.second')
      },
      {
        icon: icon_3,
        text: t('landing.jpg_to_pdf.how_to.third')
      }
    ]
  },
  title: t('meta.jpg_to_pdf.title'),
  description: t('meta.jpg_to_pdf.description'),
  fag: {
    question: 'Convert a JPG file to PDF online in a few steps!',
    answer:
      '1. To convert a JPG file, upload it using the button "Upload" above or Drag&Drop. 2. Edit your JPG document using our tools and convert it to PDF with one click. 3. Download or share the PDF file.'
  },
  name: t('meta.jpg_to_pdf.title'),
  nameKey: t('meta.jpg_to_pdf.title'),
  from: [InternalFileType.JPG, InternalFileType.JPEG],
  to: InternalFileType.PDF,
  availableFormats: '.jpg, .jpeg',
  funnel: EFunnels.JPG_TO_PDF
});

export const wordToPdfService = (t: TFunction): IService => ({
  key: 'word-to-pdf',
  serviceType: EServiceType.CONVERTOR,
  path: 'word-to-pdf',
  mainColor: 'dark-blue',
  uploadSectionImage: pdf_to_word,
  uploadSection: {
    from: InternalFileType.DOCX,
    to: InternalFileType.PDF,
    headline: t('landing.word_to_pdf.h1.title'),
    subtitle_1: t('landing.word_to_pdf.h1.subtitle')
  },
  banner: {
    headline: t('landing.word_to_pdf.CTA.title'),
    subtitle: t('landing.word_to_pdf.CTA.subtitle'),
    format: InternalFileType.DOCX,
    buttonLabel: t('landing.word_to_pdf.CTA.buttonLabel')
  },
  howTo: {
    headline_1: t('landing.word_to_pdf.how_to.h2'),
    img: how_to_pdf_to_word,
    description: [
      {
        icon: icon_1,
        text: t('landing.word_to_pdf.how_to.first')
      },
      {
        icon: icon_2,
        text: t('landing.word_to_pdf.how_to.second')
      },
      {
        icon: icon_3,
        text: t('landing.word_to_pdf.how_to.third')
      }
    ]
  },
  title: t('meta.word_to_pdf.title'),
  description: t('meta.word_to_pdf.description'),
  fag: {
    question: 'Convert a Word file to PDF online in a few steps!',
    answer:
      '1. To convert a Word file, upload it using the button "Upload" above or Drag&Drop. 2. Edit your Word document using our tools and convert it to PDF with one click. 3. Download or share the PDF file.'
  },
  name: t('meta.word_to_pdf.title'),
  nameKey: t('meta.word_to_pdf.title'),
  from: InternalFileType.DOCX,
  to: InternalFileType.PDF,
  availableFormats: '.docx, .doc',
  funnel: EFunnels.WORD_TO_PDF
});

export const pdfToEpubService = (t: TFunction): IService => ({
  key: 'pdf-to-epub',
  serviceType: EServiceType.CONVERTOR,
  path: 'pdf-to-epub',
  mainColor: 'dark-blue',
  uploadSectionImage: pdf_to_word,
  uploadSection: {
    from: InternalFileType.PDF,
    to: InternalFileType.EPUB,
    headline: t('landing.pdf_to_epub.h1.title'),
    subtitle_1: t('landing.pdf_to_epub.h1.subtitle')
  },
  banner: {
    headline: t('landing.pdf_to_epub.CTA.title'),
    subtitle: t('landing.pdf_to_epub.CTA.subtitle'),
    format: InternalFileType.PDF,
    buttonLabel: t('landing.pdf_to_epub.CTA.buttonLabel')
  },
  howTo: {
    headline_1: t('landing.pdf_to_epub.how_to.h2'),
    img: how_to_pdf_to_word,
    description: [
      {
        icon: icon_1,
        text: t('landing.pdf_to_epub.how_to.first')
      },
      {
        icon: icon_2,
        text: t('landing.pdf_to_epub.how_to.second')
      },
      {
        icon: icon_3,
        text: t('landing.pdf_to_epub.how_to.third')
      }
    ]
  },
  title: t('meta.pdf_to_epub.title'),
  description: t('meta.pdf_to_epub.description'),
  fag: {
    question: 'Convert a Word file to PDF online in a few steps!',
    answer:
      '1. To convert a Word file, upload it using the button "Upload" above or Drag&Drop. 2. Edit your Word document using our tools and convert it to PDF with one click. 3. Download or share the PDF file.'
  },
  name: t('meta.pdf_to_epub.title'),
  nameKey: t('meta.pdf_to_epub.title'),
  from: InternalFileType.PDF,
  to: InternalFileType.EPUB,
  availableFormats: '.pdf',
  funnel: EFunnels.PDF_TO_EPUB
});

export const epubToPdfService = (t: TFunction): IService => ({
  key: 'epub-to-pdf',
  serviceType: EServiceType.CONVERTOR,
  path: 'epub-to-pdf',
  mainColor: 'dark-blue',
  uploadSectionImage: pdf_to_word,
  uploadSection: {
    from: InternalFileType.EPUB,
    to: InternalFileType.PDF,
    headline: t('landing.epub_to_pdf.h1.title'),
    subtitle_1: t('landing.epub_to_pdf.h1.subtitle')
  },
  banner: {
    headline: t('landing.epub_to_pdf.CTA.title'),
    subtitle: t('landing.epub_to_pdf.CTA.subtitle'),
    format: InternalFileType.EPUB,
    buttonLabel: t('landing.epub_to_pdf.CTA.buttonLabel')
  },
  howTo: {
    headline_1: t('landing.epub_to_pdf.how_to.h2'),
    img: how_to_pdf_to_word,
    description: [
      {
        icon: icon_1,
        text: t('landing.epub_to_pdf.how_to.first')
      },
      {
        icon: icon_2,
        text: t('landing.epub_to_pdf.how_to.second')
      },
      {
        icon: icon_3,
        text: t('landing.epub_to_pdf.how_to.third')
      }
    ]
  },
  title: t('meta.epub_to_pdf.title'),
  description: t('meta.epub_to_pdf.description'),
  fag: {
    question: 'Convert a Word file to PDF online in a few steps!',
    answer:
      '1. To convert a Word file, upload it using the button "Upload" above or Drag&Drop. 2. Edit your Word document using our tools and convert it to PDF with one click. 3. Download or share the PDF file.'
  },
  name: t('meta.epub_to_pdf.title'),
  nameKey: t('meta.epub_to_pdf.title'),
  from: InternalFileType.EPUB,
  to: InternalFileType.PDF,
  availableFormats: '.epub',
  funnel: EFunnels.EPUB_TO_PDF
});

export const pdfToPngService = (t: TFunction): IService => ({
  key: 'pdf-to-png',
  serviceType: EServiceType.CONVERTOR,
  path: 'pdf-to-png',
  mainColor: 'dark-blue',
  uploadSectionImage: pdf_to_word,
  uploadSection: {
    from: InternalFileType.PDF,
    to: InternalFileType.PNG,
    headline: t('landing.pdf_to_png.h1.title'),
    subtitle_1: t('landing.pdf_to_png.h1.subtitle')
  },
  banner: {
    headline: t('landing.pdf_to_png.CTA.title'),
    subtitle: t('landing.pdf_to_png.CTA.subtitle'),
    format: InternalFileType.PDF,
    buttonLabel: t('landing.pdf_to_png.CTA.buttonLabel')
  },
  howTo: {
    headline_1: t('landing.pdf_to_png.how_to.h2'),
    img: how_to_pdf_to_word,
    description: [
      {
        icon: icon_1,
        text: t('landing.pdf_to_png.how_to.first')
      },
      {
        icon: icon_2,
        text: t('landing.pdf_to_png.how_to.second')
      },
      {
        icon: icon_3,
        text: t('landing.pdf_to_png.how_to.third')
      }
    ]
  },
  title: t('meta.pdf_to_png.title'),
  description: t('meta.pdf_to_png.description'),
  fag: {
    question: 'Convert a Word file to PDF online in a few steps!',
    answer:
      '1. To convert a Word file, upload it using the button "Upload" above or Drag&Drop. 2. Edit your Word document using our tools and convert it to PDF with one click. 3. Download or share the PDF file.'
  },
  name: t('meta.pdf_to_png.title'),
  nameKey: t('meta.pdf_to_png.title'),
  from: InternalFileType.PDF,
  to: InternalFileType.PNG,
  availableFormats: '.pdf',
  funnel: EFunnels.PDF_TO_PNG
});

export const pngToPdfService = (t: TFunction): IService => ({
  key: 'png-to-pdf',
  serviceType: EServiceType.CONVERTOR,
  path: 'png-to-pdf',
  mainColor: 'dark-blue',
  uploadSectionImage: pdf_to_word,
  uploadSection: {
    from: InternalFileType.PNG,
    to: InternalFileType.PDF,
    headline: t('landing.png_to_pdf.h1.title'),
    subtitle_1: t('landing.png_to_pdf.h1.subtitle')
  },
  banner: {
    headline: t('landing.png_to_pdf.CTA.title'),
    subtitle: t('landing.png_to_pdf.CTA.subtitle'),
    format: InternalFileType.PNG,
    buttonLabel: t('landing.png_to_pdf.CTA.buttonLabel')
  },
  howTo: {
    headline_1: t('landing.png_to_pdf.how_to.h2'),
    img: how_to_pdf_to_word,
    description: [
      {
        icon: icon_1,
        text: t('landing.png_to_pdf.how_to.first')
      },
      {
        icon: icon_2,
        text: t('landing.png_to_pdf.how_to.second')
      },
      {
        icon: icon_3,
        text: t('landing.png_to_pdf.how_to.third')
      }
    ]
  },
  title: t('meta.png_to_pdf.title'),
  description: t('meta.png_to_pdf.description'),
  fag: {
    question: 'Convert a Word file to PDF online in a few steps!',
    answer:
      '1. To convert a Word file, upload it using the button "Upload" above or Drag&Drop. 2. Edit your Word document using our tools and convert it to PDF with one click. 3. Download or share the PDF file.'
  },
  name: t('meta.png_to_pdf.title'),
  nameKey: t('meta.png_to_pdf.title'),
  from: InternalFileType.PNG,
  to: InternalFileType.PDF,
  availableFormats: '.png',
  funnel: EFunnels.PNG_TO_PDF
});

export const webpToPngService = (t: TFunction): IService => ({
  mainColor: 'orange',
  funnel: EFunnels.WEBP_TO_PNG,
  uploadSection: {
    from: InternalFileType.WEBP,
    to: InternalFileType.PNG,
    headline: t('landing.webp_to_png.h1.title'),
    subtitle_1: t('landing.webp_to_png.h1.subtitle')
  },
  banner: {
    headline: t('landing.webp_to_png.CTA.title'),
    subtitle: t('landing.webp_to_png.CTA.subtitle'),
    format: InternalFileType.WEBP,
    buttonLabel: t('landing.webp_to_png.CTA.buttonLabel')
  },
  howTo: {
    headline_1: t('landing.webp_to_png.how_to.h2'),
    subtitle_1: t('landing.webp_to_png.h1.subtitle'),
    img: how_to_pdf_to_png,
    description: [
      {
        icon: icon_1,
        text: t('landing.webp_to_png.how_to.first')
      },
      {
        icon: icon_2,
        text: t('landing.webp_to_png.how_to.second')
      },
      {
        icon: icon_3,
        text: t('landing.webp_to_png.how_to.third')
      }
    ]
  },
  title: t('meta.webp_to_png.title'),
  description: t('meta.webp_to_png.description'),
  fag: {
    question: 'Convert a WEBP file to PNG online in a few steps!',
    answer:
      '1. To convert a WEBP file, upload it using the button "Upload" above or Drag&Drop. 2. Edit your WEBP file using our tools and convert it to PNG with one click. 3. Download or share the PNG file.'
  },
  serviceType: EServiceType.CONVERTOR,
  path: 'webp-to-png',
  key: 'webp-to-png',
  name: t('meta.webp_to_png.title'),
  nameKey: 'landing.webp_to_png.name',
  from: InternalFileType.WEBP,
  to: InternalFileType.PNG,
  availableFormats: '.webp'
});

export const webpToJpgService = (t: TFunction): IService => ({
  mainColor: 'orange',
  funnel: EFunnels.WEBP_TO_JPG,
  uploadSection: {
    from: InternalFileType.WEBP,
    to: InternalFileType.JPG,
    headline: t('landing.webp_to_jpg.h1.title'),
    subtitle_1: t('landing.webp_to_jpg.h1.subtitle')
  },
  banner: {
    headline: t('landing.webp_to_jpg.CTA.title'),
    subtitle: t('landing.webp_to_jpg.CTA.subtitle'),
    format: InternalFileType.WEBP,
    buttonLabel: t('landing.webp_to_jpg.CTA.buttonLabel')
  },
  howTo: {
    headline_1: t('landing.webp_to_jpg.how_to.h2'),
    subtitle_1: t('landing.webp_to_jpg.h1.subtitle'),
    img: how_to_pdf_to_png,
    description: [
      {
        icon: icon_1,
        text: t('landing.webp_to_jpg.how_to.first')
      },
      {
        icon: icon_2,
        text: t('landing.webp_to_jpg.how_to.second')
      },
      {
        icon: icon_3,
        text: t('landing.webp_to_jpg.how_to.third')
      }
    ]
  },
  title: t('meta.webp_to_jpg.title'),
  description: t('meta.webp_to_jpg.description'),
  fag: {
    question: 'Convert a WEBP file to JPG online in a few steps!',
    answer:
      '1. To convert a WEBP file, upload it using the button "Upload" above or Drag&Drop. 2. Edit your WEBP file using our tools and convert it to JPG with one click. 3. Download or share the JPG file.'
  },
  serviceType: EServiceType.CONVERTOR,
  path: 'webp-to-jpg',
  key: 'webp-to-jpg',
  name: t('meta.webp_to_jpg.title'),
  nameKey: 'landing.webp_to_jpg.name',
  from: InternalFileType.WEBP,
  to: InternalFileType.JPG,
  availableFormats: '.webp'
});

export const heicToJpgService = (t: TFunction): IService => ({
  mainColor: 'orange',
  funnel: EFunnels.HEIC_TO_JPG,
  uploadSection: {
    from: InternalFileType.HEIC,
    to: InternalFileType.JPG,
    headline: t('landing.heic_to_jpg.h1.title'),
    subtitle_1: t('landing.heic_to_jpg.h1.subtitle')
  },
  banner: {
    headline: t('landing.heic_to_jpg.CTA.title'),
    subtitle: t('landing.heic_to_jpg.CTA.subtitle'),
    format: InternalFileType.HEIC,
    buttonLabel: t('landing.heic_to_jpg.CTA.buttonLabel')
  },
  howTo: {
    headline_1: t('landing.heic_to_jpg.how_to.h2'),
    subtitle_1: t('landing.heic_to_jpg.h1.subtitle'),
    img: how_to_pdf_to_png,
    description: [
      {
        icon: icon_1,
        text: t('landing.heic_to_jpg.how_to.first')
      },
      {
        icon: icon_2,
        text: t('landing.heic_to_jpg.how_to.second')
      },
      {
        icon: icon_3,
        text: t('landing.heic_to_jpg.how_to.third')
      }
    ]
  },
  title: t('meta.heic_to_jpg.title'),
  description: t('meta.heic_to_jpg.description'),
  fag: {
    question: 'Convert a HEIC file to JPG online in a few steps!',
    answer:
      '1. To convert a HEIC file, upload it using the button "Upload" above or Drag&Drop. 2. Edit your HEIC file using our tools and convert it to JPG with one click. 3. Download or share the JPG file.'
  },
  serviceType: EServiceType.CONVERTOR,
  path: 'heic-to-jpg',
  key: 'heic-to-jpg',
  name: t('meta.heic_to_jpg.title'),
  nameKey: 'landing.heic_to_jpg.name',
  from: InternalFileType.HEIC,
  to: InternalFileType.JPG,
  availableFormats: '.heic'
});

const jpgToPngService = (t: TFunction): IService => ({
  mainColor: 'orange',
  funnel: EFunnels.JPG_TO_PNG,
  uploadSection: {
    from: [InternalFileType.JPG, InternalFileType.JPEG],
    to: InternalFileType.PNG,
    headline: t('landing.jpg_to_png.h1.title'),
    subtitle_1: t('landing.jpg_to_png.h1.subtitle')
  },
  banner: {
    headline: t('landing.jpg_to_png.CTA.title'),
    subtitle: t('landing.jpg_to_png.CTA.subtitle'),
    format: InternalFileType.JPG,
    buttonLabel: t('landing.jpg_to_png.CTA.buttonLabel')
  },
  howTo: {
    headline_1: t('landing.jpg_to_png.how_to.h2'),
    subtitle_1: t('landing.jpg_to_png.h1.subtitle'),
    img: how_to_pdf_to_png,
    description: [
      { icon: icon_1, text: t('landing.jpg_to_png.how_to.first') },
      { icon: icon_2, text: t('landing.jpg_to_png.how_to.second') },
      { icon: icon_3, text: t('landing.jpg_to_png.how_to.third') }
    ]
  },
  title: t('meta.jpg_to_png.title'),
  description: t('meta.jpg_to_png.description'),
  fag: {
    question: 'Convert a JPG file to PNG online in a few steps!',
    answer:
      '1. To convert a JPG file, upload it using the button "Upload" above or Drag&Drop. 2. Convert it to PNG with one click. 3. Download or share the PNG file.'
  },
  serviceType: EServiceType.CONVERTOR,
  path: 'jpg-to-png',
  key: 'jpg-to-png',
  name: t('meta.jpg_to_png.title'),
  nameKey: 'landing.jpg_to_png.name',
  from: [InternalFileType.JPG, InternalFileType.JPEG],
  to: InternalFileType.PNG,
  availableFormats: '.jpg, .jpeg'
});

const avifToJpgService = (t: TFunction): IService => ({
  mainColor: 'orange',
  funnel: EFunnels.AVIF_TO_JPG,
  uploadSection: {
    from: InternalFileType.AVIF,
    to: InternalFileType.JPG,
    headline: t('landing.avif_to_jpg.h1.title'),
    subtitle_1: t('landing.avif_to_jpg.h1.subtitle')
  },
  banner: {
    headline: t('landing.avif_to_jpg.CTA.title'),
    subtitle: t('landing.avif_to_jpg.CTA.subtitle'),
    format: InternalFileType.AVIF,
    buttonLabel: t('landing.avif_to_jpg.CTA.buttonLabel')
  },
  howTo: {
    headline_1: t('landing.avif_to_jpg.how_to.h2'),
    subtitle_1: t('landing.avif_to_jpg.h1.subtitle'),
    img: how_to_pdf_to_png,
    description: [
      { icon: icon_1, text: t('landing.avif_to_jpg.how_to.first') },
      { icon: icon_2, text: t('landing.avif_to_jpg.how_to.second') },
      { icon: icon_3, text: t('landing.avif_to_jpg.how_to.third') }
    ]
  },
  title: t('meta.avif_to_jpg.title'),
  description: t('meta.avif_to_jpg.description'),
  fag: {
    question: 'Convert an AVIF file to JPG online in a few steps!',
    answer: '1. Upload an AVIF image. 2. Convert it to JPG with one click. 3. Download or share the JPG file.'
  },
  serviceType: EServiceType.CONVERTOR,
  path: 'avif-to-jpg',
  key: 'avif-to-jpg',
  name: t('meta.avif_to_jpg.title'),
  nameKey: 'landing.avif_to_jpg.name',
  from: InternalFileType.AVIF,
  to: InternalFileType.JPG,
  availableFormats: '.avif'
});

const svgToPngService = (t: TFunction): IService => ({
  mainColor: 'orange',
  funnel: EFunnels.SVG_TO_PNG,
  uploadSection: {
    from: InternalFileType.SVG,
    to: InternalFileType.PNG,
    headline: t('landing.svg_to_png.h1.title'),
    subtitle_1: t('landing.svg_to_png.h1.subtitle')
  },
  banner: {
    headline: t('landing.svg_to_png.CTA.title'),
    subtitle: t('landing.svg_to_png.CTA.subtitle'),
    format: InternalFileType.SVG,
    buttonLabel: t('landing.svg_to_png.CTA.buttonLabel')
  },
  howTo: {
    headline_1: t('landing.svg_to_png.how_to.h2'),
    subtitle_1: t('landing.svg_to_png.h1.subtitle'),
    img: how_to_pdf_to_png,
    description: [
      { icon: icon_1, text: t('landing.svg_to_png.how_to.first') },
      { icon: icon_2, text: t('landing.svg_to_png.how_to.second') },
      { icon: icon_3, text: t('landing.svg_to_png.how_to.third') }
    ]
  },
  title: t('meta.svg_to_png.title'),
  description: t('meta.svg_to_png.description'),
  fag: {
    question: 'Convert an SVG file to PNG online in a few steps!',
    answer: '1. Upload an SVG image. 2. Convert it to PNG with one click. 3. Download or share the PNG file.'
  },
  serviceType: EServiceType.CONVERTOR,
  path: 'svg-to-png',
  key: 'svg-to-png',
  name: t('meta.svg_to_png.title'),
  nameKey: 'landing.svg_to_png.name',
  from: InternalFileType.SVG,
  to: InternalFileType.PNG,
  availableFormats: '.svg'
});

const avifToPngService = (t: TFunction): IService => ({
  mainColor: 'orange',
  funnel: EFunnels.AVIF_TO_PNG,
  uploadSection: {
    from: InternalFileType.AVIF,
    to: InternalFileType.PNG,
    headline: t('landing.avif_to_png.h1.title'),
    subtitle_1: t('landing.avif_to_png.h1.subtitle')
  },
  banner: {
    headline: t('landing.avif_to_png.CTA.title'),
    subtitle: t('landing.avif_to_png.CTA.subtitle'),
    format: InternalFileType.AVIF,
    buttonLabel: t('landing.avif_to_png.CTA.buttonLabel')
  },
  howTo: {
    headline_1: t('landing.avif_to_png.how_to.h2'),
    subtitle_1: t('landing.avif_to_png.h1.subtitle'),
    img: how_to_pdf_to_png,
    description: [
      { icon: icon_1, text: t('landing.avif_to_png.how_to.first') },
      { icon: icon_2, text: t('landing.avif_to_png.how_to.second') },
      { icon: icon_3, text: t('landing.avif_to_png.how_to.third') }
    ]
  },
  title: t('meta.avif_to_png.title'),
  description: t('meta.avif_to_png.description'),
  fag: {
    question: 'Convert an AVIF file to PNG online in a few steps!',
    answer: '1. Upload an AVIF image. 2. Convert it to PNG with one click. 3. Download or share the PNG file.'
  },
  serviceType: EServiceType.CONVERTOR,
  path: 'avif-to-png',
  key: 'avif-to-png',
  name: t('meta.avif_to_png.title'),
  nameKey: 'landing.avif_to_png.name',
  from: InternalFileType.AVIF,
  to: InternalFileType.PNG,
  availableFormats: '.avif'
});

const jfifToJpgService = (t: TFunction): IService => ({
  mainColor: 'orange',
  funnel: EFunnels.JFIF_TO_JPG,
  uploadSection: {
    from: InternalFileType.JFIF,
    to: InternalFileType.JPG,
    headline: t('landing.jfif_to_jpg.h1.title'),
    subtitle_1: t('landing.jfif_to_jpg.h1.subtitle')
  },
  banner: {
    headline: t('landing.jfif_to_jpg.CTA.title'),
    subtitle: t('landing.jfif_to_jpg.CTA.subtitle'),
    format: InternalFileType.JFIF,
    buttonLabel: t('landing.jfif_to_jpg.CTA.buttonLabel')
  },
  howTo: {
    headline_1: t('landing.jfif_to_jpg.how_to.h2'),
    subtitle_1: t('landing.jfif_to_jpg.h1.subtitle'),
    img: how_to_pdf_to_png,
    description: [
      { icon: icon_1, text: t('landing.jfif_to_jpg.how_to.first') },
      { icon: icon_2, text: t('landing.jfif_to_jpg.how_to.second') },
      { icon: icon_3, text: t('landing.jfif_to_jpg.how_to.third') }
    ]
  },
  title: t('meta.jfif_to_jpg.title'),
  description: t('meta.jfif_to_jpg.description'),
  fag: {
    question: 'Convert a JFIF file to JPG online in a few steps!',
    answer: '1. Upload a JFIF image. 2. Convert it to JPG with one click. 3. Download or share the JPG file.'
  },
  serviceType: EServiceType.CONVERTOR,
  path: 'jfif-to-jpg',
  key: 'jfif-to-jpg',
  name: t('meta.jfif_to_jpg.title'),
  nameKey: 'landing.jfif_to_jpg.name',
  from: InternalFileType.JFIF,
  to: InternalFileType.JPG,
  availableFormats: '.jfif'
});

const htmlToPdfService = (t: TFunction): IService => ({
  key: 'html-to-pdf',
  serviceType: EServiceType.CONVERTOR,
  path: 'html-to-pdf',
  mainColor: 'dark-blue',
  uploadSectionImage: pdf_to_word,
  uploadSection: {
    from: InternalFileType.HTML,
    to: InternalFileType.PDF,
    headline: t('landing.html_to_pdf.h1.title'),
    subtitle_1: t('landing.html_to_pdf.h1.subtitle')
  },
  banner: {
    headline: t('landing.html_to_pdf.CTA.title'),
    subtitle: t('landing.html_to_pdf.CTA.subtitle'),
    format: InternalFileType.HTML,
    buttonLabel: t('landing.html_to_pdf.CTA.buttonLabel')
  },
  howTo: {
    headline_1: t('landing.html_to_pdf.how_to.h2'),
    img: how_to_pdf_to_word,
    description: [
      { icon: icon_1, text: t('landing.html_to_pdf.how_to.first') },
      { icon: icon_2, text: t('landing.html_to_pdf.how_to.second') },
      { icon: icon_3, text: t('landing.html_to_pdf.how_to.third') }
    ]
  },
  title: t('meta.html_to_pdf.title'),
  description: t('meta.html_to_pdf.description'),
  fag: {
    question: 'Convert an HTML file to PDF online in a few steps!',
    answer: '1. Upload an HTML file. 2. Convert it to PDF with one click. 3. Download or share the PDF file.'
  },
  name: t('meta.html_to_pdf.title'),
  nameKey: 'landing.html_to_pdf.name',
  from: InternalFileType.HTML,
  to: InternalFileType.PDF,
  availableFormats: 'text/html',
  funnel: EFunnels.HTML_TO_PDF
});

const pngToWebpService = (t: TFunction): IService => ({
  mainColor: 'orange',
  funnel: EFunnels.PNG_TO_WEBP,
  uploadSection: {
    from: InternalFileType.PNG,
    to: InternalFileType.WEBP,
    headline: t('landing.png_to_webp.h1.title'),
    subtitle_1: t('landing.png_to_webp.h1.subtitle')
  },
  banner: {
    headline: t('landing.png_to_webp.CTA.title'),
    subtitle: t('landing.png_to_webp.CTA.subtitle'),
    format: InternalFileType.PNG,
    buttonLabel: t('landing.png_to_webp.CTA.buttonLabel')
  },
  howTo: {
    headline_1: t('landing.png_to_webp.how_to.h2'),
    subtitle_1: t('landing.png_to_webp.h1.subtitle'),
    img: how_to_pdf_to_png,
    description: [
      { icon: icon_1, text: t('landing.png_to_webp.how_to.first') },
      { icon: icon_2, text: t('landing.png_to_webp.how_to.second') },
      { icon: icon_3, text: t('landing.png_to_webp.how_to.third') }
    ]
  },
  title: t('meta.png_to_webp.title'),
  description: t('meta.png_to_webp.description'),
  fag: {
    question: 'Convert a PNG file to WEBP online in a few steps!',
    answer: '1. Upload a PNG image. 2. Convert it to WEBP with one click. 3. Download or share the WEBP file.'
  },
  serviceType: EServiceType.CONVERTOR,
  path: 'png-to-webp',
  key: 'png-to-webp',
  name: t('meta.png_to_webp.title'),
  nameKey: 'landing.png_to_webp.name',
  from: InternalFileType.PNG,
  to: InternalFileType.WEBP,
  availableFormats: '.png'
});

const heicToPngService = (t: TFunction): IService => ({
  mainColor: 'orange',
  funnel: EFunnels.HEIC_TO_PNG,
  uploadSection: {
    from: InternalFileType.HEIC,
    to: InternalFileType.PNG,
    headline: t('landing.heic_to_png.h1.title'),
    subtitle_1: t('landing.heic_to_png.h1.subtitle')
  },
  banner: {
    headline: t('landing.heic_to_png.CTA.title'),
    subtitle: t('landing.heic_to_png.CTA.subtitle'),
    format: InternalFileType.HEIC,
    buttonLabel: t('landing.heic_to_png.CTA.buttonLabel')
  },
  howTo: {
    headline_1: t('landing.heic_to_png.how_to.h2'),
    subtitle_1: t('landing.heic_to_png.h1.subtitle'),
    img: how_to_pdf_to_png,
    description: [
      { icon: icon_1, text: t('landing.heic_to_png.how_to.first') },
      { icon: icon_2, text: t('landing.heic_to_png.how_to.second') },
      { icon: icon_3, text: t('landing.heic_to_png.how_to.third') }
    ]
  },
  title: t('meta.heic_to_png.title'),
  description: t('meta.heic_to_png.description'),
  fag: {
    question: 'Convert a HEIC file to PNG online in a few steps!',
    answer: '1. Upload a HEIC image. 2. Convert it to PNG with one click. 3. Download or share the PNG file.'
  },
  serviceType: EServiceType.CONVERTOR,
  path: 'heic-to-png',
  key: 'heic-to-png',
  name: t('meta.heic_to_png.title'),
  nameKey: 'landing.heic_to_png.name',
  from: InternalFileType.HEIC,
  to: InternalFileType.PNG,
  availableFormats: '.heic'
});

const csvToExcelService = (t: TFunction): IService => ({
  key: 'csv-to-excel',
  serviceType: EServiceType.CONVERTOR,
  path: 'csv-to-excel',
  mainColor: 'green',
  uploadSectionImage: pdf_to_excel,
  uploadSection: {
    from: InternalFileType.CSV,
    to: InternalFileType.XLS,
    headline: t('landing.csv_to_excel.h1.title'),
    subtitle_1: t('landing.csv_to_excel.h1.subtitle')
  },
  banner: {
    headline: t('landing.csv_to_excel.CTA.title'),
    subtitle: t('landing.csv_to_excel.CTA.subtitle'),
    format: InternalFileType.CSV,
    buttonLabel: t('landing.csv_to_excel.CTA.buttonLabel')
  },
  howTo: {
    headline_1: t('landing.csv_to_excel.how_to.h2'),
    subtitle_1: t('landing.csv_to_excel.h1.subtitle'),
    img: how_to_pdf_to_excel,
    description: [
      { icon: icon_1, text: t('landing.csv_to_excel.how_to.first') },
      { icon: icon_2, text: t('landing.csv_to_excel.how_to.second') },
      { icon: icon_3, text: t('landing.csv_to_excel.how_to.third') }
    ]
  },
  title: t('meta.csv_to_excel.title'),
  description: t('meta.csv_to_excel.description'),
  fag: {
    question: 'Convert a CSV file to Excel online in a few steps!',
    answer: '1. Upload a CSV file. 2. Convert it to Excel with one click. 3. Download or share the Excel file.'
  },
  name: t('meta.csv_to_excel.title'),
  nameKey: 'landing.csv_to_excel.name',
  from: InternalFileType.CSV,
  to: InternalFileType.XLS,
  availableFormats: '.csv',
  funnel: EFunnels.CSV_TO_EXCEL
});

const dwgToPdfService = (t: TFunction): IService => ({
  key: 'dwg-to-pdf',
  serviceType: EServiceType.CONVERTOR,
  path: 'dwg-to-pdf',
  mainColor: 'dark-blue',
  uploadSectionImage: pdf_to_word,
  uploadSection: {
    from: InternalFileType.DWG,
    to: InternalFileType.PDF,
    headline: t('landing.dwg_to_pdf.h1.title'),
    subtitle_1: t('landing.dwg_to_pdf.h1.subtitle')
  },
  banner: {
    headline: t('landing.dwg_to_pdf.CTA.title'),
    subtitle: t('landing.dwg_to_pdf.CTA.subtitle'),
    format: InternalFileType.DWG,
    buttonLabel: t('landing.dwg_to_pdf.CTA.buttonLabel')
  },
  howTo: {
    headline_1: t('landing.dwg_to_pdf.how_to.h2'),
    img: how_to_pdf_to_word,
    description: [
      { icon: icon_1, text: t('landing.dwg_to_pdf.how_to.first') },
      { icon: icon_2, text: t('landing.dwg_to_pdf.how_to.second') },
      { icon: icon_3, text: t('landing.dwg_to_pdf.how_to.third') }
    ]
  },
  title: t('meta.dwg_to_pdf.title'),
  description: t('meta.dwg_to_pdf.description'),
  fag: {
    question: 'Convert a DWG file to PDF online in a few steps!',
    answer: '1. Upload a DWG file. 2. Convert it to PDF with one click. 3. Download or share the PDF file.'
  },
  name: t('meta.dwg_to_pdf.title'),
  nameKey: 'landing.dwg_to_pdf.name',
  from: InternalFileType.DWG,
  to: InternalFileType.PDF,
  availableFormats: 'image/vnd.dwg,.dwg',
  funnel: EFunnels.DWG_TO_PDF
});

const textToPdfService = (t: TFunction): IService => ({
  key: 'text-to-pdf',
  serviceType: EServiceType.CONVERTOR,
  path: 'text-to-pdf',
  mainColor: 'dark-blue',
  uploadSectionImage: pdf_to_word,
  uploadSection: {
    from: [InternalFileType.TEXT, InternalFileType.TXT],
    to: InternalFileType.PDF,
    headline: t('landing.text_to_pdf.h1.title'),
    subtitle_1: t('landing.text_to_pdf.h1.subtitle')
  },
  banner: {
    headline: t('landing.text_to_pdf.CTA.title'),
    subtitle: t('landing.text_to_pdf.CTA.subtitle'),
    format: InternalFileType.TEXT,
    buttonLabel: t('landing.text_to_pdf.CTA.buttonLabel')
  },
  howTo: {
    headline_1: t('landing.text_to_pdf.how_to.h2'),
    img: how_to_pdf_to_word,
    description: [
      { icon: icon_1, text: t('landing.text_to_pdf.how_to.first') },
      { icon: icon_2, text: t('landing.text_to_pdf.how_to.second') },
      { icon: icon_3, text: t('landing.text_to_pdf.how_to.third') }
    ]
  },
  title: t('meta.text_to_pdf.title'),
  description: t('meta.text_to_pdf.description'),
  fag: {
    question: 'Convert a text file to PDF online in a few steps!',
    answer: '1. Upload a TXT file. 2. Convert it to PDF with one click. 3. Download or share the PDF file.'
  },
  name: t('meta.text_to_pdf.title'),
  nameKey: 'landing.text_to_pdf.name',
  from: [InternalFileType.TEXT, InternalFileType.TXT],
  to: InternalFileType.PDF,
  availableFormats: 'text/plain,.text,.txt',
  funnel: EFunnels.TEXT_TO_PDF
});

const tiffToPdfService = (t: TFunction): IService => ({
  key: 'tiff-to-pdf',
  serviceType: EServiceType.CONVERTOR,
  path: 'tiff-to-pdf',
  mainColor: 'dark-blue',
  uploadSectionImage: pdf_to_word,
  uploadSection: {
    from: InternalFileType.TIFF,
    to: InternalFileType.PDF,
    headline: t('landing.tiff_to_pdf.h1.title'),
    subtitle_1: t('landing.tiff_to_pdf.h1.subtitle')
  },
  banner: {
    headline: t('landing.tiff_to_pdf.CTA.title'),
    subtitle: t('landing.tiff_to_pdf.CTA.subtitle'),
    format: InternalFileType.TIFF,
    buttonLabel: t('landing.tiff_to_pdf.CTA.buttonLabel')
  },
  howTo: {
    headline_1: t('landing.tiff_to_pdf.how_to.h2'),
    img: how_to_pdf_to_word,
    description: [
      { icon: icon_1, text: t('landing.tiff_to_pdf.how_to.first') },
      { icon: icon_2, text: t('landing.tiff_to_pdf.how_to.second') },
      { icon: icon_3, text: t('landing.tiff_to_pdf.how_to.third') }
    ]
  },
  title: t('meta.tiff_to_pdf.title'),
  description: t('meta.tiff_to_pdf.description'),
  fag: {
    question: 'Convert a TIFF file to PDF online in a few steps!',
    answer: '1. Upload a TIFF image. 2. Convert it to PDF with one click. 3. Download or share the PDF file.'
  },
  name: t('meta.tiff_to_pdf.title'),
  nameKey: 'landing.tiff_to_pdf.name',
  from: InternalFileType.TIFF,
  to: InternalFileType.PDF,
  availableFormats: '.tiff',
  funnel: EFunnels.TIFF_TO_PDF
});

const djvuToPdfService = (t: TFunction): IService => ({
  key: 'djvu-to-pdf',
  serviceType: EServiceType.CONVERTOR,
  path: 'djvu-to-pdf',
  mainColor: 'dark-blue',
  uploadSectionImage: pdf_to_word,
  uploadSection: {
    from: InternalFileType.DJVU,
    to: InternalFileType.PDF,
    headline: t('landing.djvu_to_pdf.h1.title'),
    subtitle_1: t('landing.djvu_to_pdf.h1.subtitle')
  },
  banner: {
    headline: t('landing.djvu_to_pdf.CTA.title'),
    subtitle: t('landing.djvu_to_pdf.CTA.subtitle'),
    format: InternalFileType.DJVU,
    buttonLabel: t('landing.djvu_to_pdf.CTA.buttonLabel')
  },
  howTo: {
    headline_1: t('landing.djvu_to_pdf.how_to.h2'),
    img: how_to_pdf_to_word,
    description: [
      { icon: icon_1, text: t('landing.djvu_to_pdf.how_to.first') },
      { icon: icon_2, text: t('landing.djvu_to_pdf.how_to.second') },
      { icon: icon_3, text: t('landing.djvu_to_pdf.how_to.third') }
    ]
  },
  title: t('meta.djvu_to_pdf.title'),
  description: t('meta.djvu_to_pdf.description'),
  fag: {
    question: 'Convert a DJVU file to PDF online in a few steps!',
    answer: '1. Upload a DJVU file. 2. Convert it to PDF with one click. 3. Download or share the PDF file.'
  },
  name: t('meta.djvu_to_pdf.title'),
  nameKey: 'landing.djvu_to_pdf.name',
  from: InternalFileType.DJVU,
  to: InternalFileType.PDF,
  availableFormats: '.djvu',
  funnel: EFunnels.DJVU_TO_PDF
});

const svgToJpgService = (t: TFunction): IService => ({
  mainColor: 'orange',
  funnel: EFunnels.SVG_TO_JPG,
  uploadSection: {
    from: InternalFileType.SVG,
    to: InternalFileType.JPG,
    headline: t('landing.svg_to_jpg.h1.title'),
    subtitle_1: t('landing.svg_to_jpg.h1.subtitle')
  },
  banner: {
    headline: t('landing.svg_to_jpg.CTA.title'),
    subtitle: t('landing.svg_to_jpg.CTA.subtitle'),
    format: InternalFileType.SVG,
    buttonLabel: t('landing.svg_to_jpg.CTA.buttonLabel')
  },
  howTo: {
    headline_1: t('landing.svg_to_jpg.how_to.h2'),
    subtitle_1: t('landing.svg_to_jpg.h1.subtitle'),
    img: how_to_pdf_to_png,
    description: [
      { icon: icon_1, text: t('landing.svg_to_jpg.how_to.first') },
      { icon: icon_2, text: t('landing.svg_to_jpg.how_to.second') },
      { icon: icon_3, text: t('landing.svg_to_jpg.how_to.third') }
    ]
  },
  title: t('meta.svg_to_jpg.title'),
  description: t('meta.svg_to_jpg.description'),
  fag: {
    question: 'Convert an SVG file to JPG online in a few steps!',
    answer: '1. Upload an SVG image. 2. Convert it to JPG with one click. 3. Download or share the JPG file.'
  },
  serviceType: EServiceType.CONVERTOR,
  path: 'svg-to-jpg',
  key: 'svg-to-jpg',
  name: t('meta.svg_to_jpg.title'),
  nameKey: 'landing.svg_to_jpg.name',
  from: InternalFileType.SVG,
  to: InternalFileType.JPG,
  availableFormats: '.svg'
});

const epubToMobiService = (t: TFunction): IService => ({
  key: 'epub-to-mobi',
  serviceType: EServiceType.CONVERTOR,
  path: 'epub-to-mobi',
  mainColor: 'dark-blue',
  uploadSectionImage: pdf_to_word,
  uploadSection: {
    from: InternalFileType.EPUB,
    to: InternalFileType.MOBI,
    headline: t('landing.epub_to_mobi.h1.title'),
    subtitle_1: t('landing.epub_to_mobi.h1.subtitle')
  },
  banner: {
    headline: t('landing.epub_to_mobi.CTA.title'),
    subtitle: t('landing.epub_to_mobi.CTA.subtitle'),
    format: InternalFileType.EPUB,
    buttonLabel: t('landing.epub_to_mobi.CTA.buttonLabel')
  },
  howTo: {
    headline_1: t('landing.epub_to_mobi.how_to.h2'),
    img: how_to_pdf_to_word,
    description: [
      { icon: icon_1, text: t('landing.epub_to_mobi.how_to.first') },
      { icon: icon_2, text: t('landing.epub_to_mobi.how_to.second') },
      { icon: icon_3, text: t('landing.epub_to_mobi.how_to.third') }
    ]
  },
  title: t('meta.epub_to_mobi.title'),
  description: t('meta.epub_to_mobi.description'),
  fag: {
    question: 'Convert an EPUB file to MOBI online in a few steps!',
    answer: '1. Upload an EPUB file. 2. Convert it to MOBI with one click. 3. Download or share the MOBI file.'
  },
  name: t('meta.epub_to_mobi.title'),
  nameKey: 'landing.epub_to_mobi.name',
  from: InternalFileType.EPUB,
  to: InternalFileType.MOBI,
  availableFormats: '.epub',
  funnel: EFunnels.EPUB_TO_MOBI
});

const gifToPngService = (t: TFunction): IService => ({
  mainColor: 'orange',
  funnel: EFunnels.GIF_TO_PNG,
  uploadSection: {
    from: InternalFileType.GIF,
    to: InternalFileType.PNG,
    headline: t('landing.gif_to_png.h1.title'),
    subtitle_1: t('landing.gif_to_png.h1.subtitle')
  },
  banner: {
    headline: t('landing.gif_to_png.CTA.title'),
    subtitle: t('landing.gif_to_png.CTA.subtitle'),
    format: InternalFileType.GIF,
    buttonLabel: t('landing.gif_to_png.CTA.buttonLabel')
  },
  howTo: {
    headline_1: t('landing.gif_to_png.how_to.h2'),
    subtitle_1: t('landing.gif_to_png.h1.subtitle'),
    img: how_to_pdf_to_png,
    description: [
      { icon: icon_1, text: t('landing.gif_to_png.how_to.first') },
      { icon: icon_2, text: t('landing.gif_to_png.how_to.second') },
      { icon: icon_3, text: t('landing.gif_to_png.how_to.third') }
    ]
  },
  title: t('meta.gif_to_png.title'),
  description: t('meta.gif_to_png.description'),
  fag: {
    question: 'Convert a GIF file to PNG online in a few steps!',
    answer: '1. Upload a GIF image. 2. Convert it to PNG with one click. 3. Download or share the PNG file.'
  },
  serviceType: EServiceType.CONVERTOR,
  path: 'gif-to-png',
  key: 'gif-to-png',
  name: t('meta.gif_to_png.title'),
  nameKey: 'landing.gif_to_png.name',
  from: InternalFileType.GIF,
  to: InternalFileType.PNG,
  availableFormats: '.gif'
});

export const jpgToWebpService = (t: TFunction): IService => ({
  mainColor: 'orange',
  funnel: EFunnels.JPG_TO_WEBP,
  uploadSection: {
    from: InternalFileType.JPG,
    to: InternalFileType.WEBP,
    headline: t('landing.jpg_to_webp.h1.title'),
    subtitle_1: t('landing.jpg_to_webp.h1.subtitle')
  },
  banner: {
    headline: t('landing.jpg_to_webp.CTA.title'),
    subtitle: t('landing.jpg_to_webp.CTA.subtitle'),
    format: InternalFileType.JPG,
    buttonLabel: t('landing.jpg_to_webp.CTA.buttonLabel')
  },
  howTo: {
    headline_1: t('landing.jpg_to_webp.how_to.h2'),
    subtitle_1: t('landing.jpg_to_webp.h1.subtitle'),
    img: how_to_pdf_to_png,
    description: [
      {
        icon: icon_1,
        text: t('landing.jpg_to_webp.how_to.first')
      },
      {
        icon: icon_2,
        text: t('landing.jpg_to_webp.how_to.second')
      },
      {
        icon: icon_3,
        text: t('landing.jpg_to_webp.how_to.third')
      }
    ]
  },
  title: t('meta.jpg_to_webp.title'),
  description: t('meta.jpg_to_webp.description'),
  fag: {
    question: 'Convert a JPG file to WEBP online in a few steps!',
    answer:
      '1. To convert a WEBP file, upload it using the button "Upload" above or Drag&Drop. 2. Edit your WEBP file using our tools and convert it to JPG with one click. 3. Download or share the JPG file.'
  },
  serviceType: EServiceType.CONVERTOR,
  path: 'jpg-to-webp',
  key: 'jpg-to-webp',
  name: t('meta.webp_to_jpg.title'),
  nameKey: 'landing.jpg_to_webp.name',
  from: [InternalFileType.JPG, InternalFileType.JPEG],
  to: InternalFileType.WEBP,
  availableFormats: '.jpg, .jpeg'
});

export const converterServicesList = (t: TFunction): IService[] => {
  return [
    mergePageService(t),
    imageToPdfService(t),
    mergeImagesPageService(t),
    pdfToExcelService(t),
    pdfToWordService(t),
    pdfToPptxService(t),
    pdfConverterService(t),
    pdfToJpgService(t),
    jpgToPdfService(t),
    wordToPdfService(t),
    pdfToEpubService(t),
    epubToPdfService(t),
    pdfToPngService(t),
    pngToPdfService(t),
    jpgToWebpService(t),
    webpToJpgService(t),
    webpToPngService(t),
    heicToJpgService(t),
    jpgToPngService(t),
    avifToJpgService(t),
    svgToPngService(t),
    avifToPngService(t),
    jfifToJpgService(t),
    htmlToPdfService(t),
    pngToWebpService(t),
    heicToPngService(t),
    csvToExcelService(t),
    dwgToPdfService(t),
    textToPdfService(t),
    tiffToPdfService(t),
    djvuToPdfService(t),
    svgToJpgService(t),
    epubToMobiService(t),
    gifToPngService(t)
  ];
};
